/**
 * Geohash Utility Functions for CrapperMapper
 * 
 * MVP Implementation - Core geohash functionality for spatial indexing
 * Using ngeohash library with 7-character precision (~150m accuracy)
 * 
 * Future enhancements: validation, normalization, advanced viewport coverage
 */

import Geohash from 'ngeohash';

export const DEFAULT_PRECISION = 7; // ~150m accuracy at 7 characters

/**
 * Core encoding/decoding functions
 */

/**
 * Encodes latitude/longitude into a geohash string
 * @param {number} lat - Latitude (-90 to 90)
 * @param {number} lng - Longitude (-180 to 180) 
 * @param {number} precision - Geohash precision (default: 7)
 * @returns {string} Geohash string
 */
export const generateGeohash = (lat, lng, precision = DEFAULT_PRECISION) => {
  return Geohash.encode(lat, lng, precision);
};

/**
 * Decodes a geohash into its rectangular bounds
 * @param {string} geohash - Geohash string to decode
 * @returns {object} Bounds object: { minLat, maxLat, minLng, maxLng }
 */
export const getGeohashBounds = (geohash) => {
  return Geohash.decode_bbox(geohash);
};

/**
 * Precision and zoom strategy
 */

/**
 * Maps Leaflet zoom levels to appropriate geohash precision
 * @param {number} zoomLevel - Map zoom level (typically 1-18)
 * @returns {number} Recommended geohash precision
 */
export const getPrecisionForZoom = (zoomLevel) => {
  if (zoomLevel > 16) return 7; // Very zoomed in (~150m)
  if (zoomLevel > 13) return 6; // City level (~1.2km)
  if (zoomLevel > 10) return 5; // Regional level (~4.9km)
  if (zoomLevel > 7) return 4;  // State level (~39km)
  if (zoomLevel > 4) return 3;  // Country level (~156km)
  return 2; // Continent level (~1250km)
};

/**
 * Neighboring cells and coverage
 */

/**
 * Returns the 8 neighboring geohashes around the given geohash
 * @param {string} geohash - Center geohash
 * @returns {string[]} Array of 8 neighboring geohashes
 */
export const getNeighbors = (geohash) => {
  return Geohash.neighbors(geohash);
};

/**
 * Returns neighborhood including center and neighbors
 * @param {string} geohash - Center geohash
 * @param {boolean} includeSelf - Whether to include the center geohash
 * @returns {string[]} Array of geohashes covering the area
 */
export const getNeighborhood = (geohash, includeSelf = true) => {
  const neighbors = getNeighbors(geohash);
  return includeSelf ? [geohash, ...neighbors] : neighbors;
};

/**
 * Firestore-friendly range helpers
 */

/**
 * Creates Firestore lexicographic range for geohash prefix queries
 * @param {string} prefix - Geohash prefix
 * @returns {object} Range object: { start, end }
 */
export const getGeohashRangeForPrefix = (prefix) => {
  return {
    start: prefix,
    end: prefix + '\uffff' // Highest Unicode character
  };
};

/**
 * Viewport-driven loading
 */

/**
 * Gets geohash prefixes needed to cover current map viewport
 * @param {object} bounds - Map bounds object (must have getNorthEast and getSouthWest)
 * @param {number} zoomLevel - Current map zoom level
 * @returns {string[]} Array of geohash prefixes to query
 */
export const getGeohashPrefixesForViewport = (bounds, zoomLevel) => {
  if (!bounds || typeof bounds.getNorthEast !== 'function') {
    return [];
  }

  const precision = getPrecisionForZoom(zoomLevel);
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();

  // Use bboxes integer coverage to find all geohashes in the viewport
  // This ensures better coverage than just center + neighbors
  //
  // ngeohash throws RangeError when the cell count explodes (fine precision
  // over a huge viewport), so reduce precision until bboxes both succeeds
  // and stays under the query limit instead of crashing the caller.
  let prefixes = [];
  let currentPrecision = precision;
  for (;;) {
    try {
      prefixes = Geohash.bboxes(
        sw.lat(),
        sw.lng(),
        ne.lat(),
        ne.lng(),
        currentPrecision,
      );
    } catch (error) {
      // Too many cells to allocate: treat as over-limit and go coarser.
      prefixes = null;
    }
    if (prefixes && prefixes.length <= 12) {
      break;
    }
    if (currentPrecision <= 2) {
      prefixes = prefixes || [];
      break;
    }
    currentPrecision--;
  }

  return prefixes;
};

/**
 * Distance and filtering utilities
 */

/**
 * Calculates great-circle distance between two points in miles
 * @param {number} lat1 - First point latitude
 * @param {number} lng1 - First point longitude  
 * @param {number} lat2 - Second point latitude
 * @param {number} lng2 - Second point longitude
 * @returns {number} Distance in miles
 */
export const haversineMiles = (lat1, lng1, lat2, lng2) => {
  const R = 3958.8; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

/**
 * Data merge helpers for multi-query fetching
 */

/**
 * Removes duplicate documents by ID field
 * @param {Array} docs - Array of document objects
 * @param {string} idField - Field name to use for deduplication (default: "id")
 * @returns {Array} Deduplicated array
 */
export const dedupeById = (docs, idField = "id") => {
  const seen = new Set();
  return docs.filter(doc => {
    if (seen.has(doc[idField])) return false;
    seen.add(doc[idField]);
    return true;
  });
};

/**
 * Example usage and testing helpers
 */


export default {
  generateGeohash,
  getGeohashBounds,
  getPrecisionForZoom,
  getNeighbors,
  getNeighborhood,
  getGeohashRangeForPrefix,
  getGeohashPrefixesForViewport,
  haversineMiles,
  dedupeById
};
